import {useState, useEffect, useRef, useCallback} from 'react'
import type {NoiseGateProcessor} from './noiseGateProcessor'

/**
 * Subscribes to the NoiseGateProcessor's real-time audio level.
 * Returns the current RMS value and the auto-computed threshold,
 * throttled to ~30 fps React state updates to avoid excessive re-renders.
 */
export function useLocalAudioLevel(processor: NoiseGateProcessor | null) {
  const [level, setLevel] = useState(0)
  const [autoThreshold, setAutoThreshold] = useState(0)
  const lastUpdateRef = useRef(0)

  const handleLevel = useCallback(
    (rms: number) => {
      const now = performance.now()
      // Throttle state updates to ~30 fps (every ~33ms)
      if (now - lastUpdateRef.current > 33) {
        lastUpdateRef.current = now
        setLevel(rms)
        // Read auto-threshold from the processor at the same cadence
        if (processor) {
          setAutoThreshold(processor.autoThreshold)
        }
      }
    },
    [processor],
  )

  useEffect(() => {
    if (!processor) return

    processor.setLevelCallback(handleLevel)
    return () => {
      processor.setLevelCallback(null)
    }
  }, [processor, handleLevel])

  return {level, autoThreshold}
}
